// Chapter 31 - Ethical and Professional Responsibilities of Applied Behavior Analysts
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 31 (Freeman, LeBlanc, & Martinez-Diaz).

import type { ChapterModule } from "./types";

const chapter31Module: ChapterModule = {
  meta: {
    id: "31",
    number: 31,
    title: "Ethical and Professional Responsibilities of Applied Behavior Analysts",
    subtitle: "BACB Ethics Code, informed consent, scope of competence",
    part: "Part 13 - Ethics",
    estMinutes: 90,
    bacb: ["E-1", "E-2", "E-3", "E-4", "E-5", "E-6", "E-7", "E-8", "E-9", "E-10"],
  },
  curated: true,
  overview: [
    "Chapter 31 (Freeman, LeBlanc, & Martinez-Diaz) addresses three fundamental ethics questions: What is the right thing to do? What is worth doing? What does it mean to be a good behavior analyst? Ethics in ABA is both DEONTOLOGICAL (the act itself) and UTILITARIAN (the results); both contribute to ethical conduct.",
    "The chapter covers professional codes of conduct (BACB Compliance Code and related documents), ensuring professional competence (formal training, supervised practica, mentored experience, scope of competence), the core client-services ethics (informed consent, confidentiality, client dignity/health/safety, rights), coordination with other professionals, social media and new technologies, advocacy for the client (right to effective treatment), conflict of interest, and creating a culture of ethical practice.",
  ],
  objectives: [
    { id: "E-1",  text: "Behave in accord with the BACB Ethical Code." },
    { id: "E-2",  text: "Respond appropriately to ethical violations." },
    { id: "E-3",  text: "Practice within one's scope of competence." },
    { id: "E-4",  text: "Maintain professional competence through ongoing education." },
    { id: "E-5",  text: "Obtain informed consent." },
    { id: "E-6",  text: "Maintain confidentiality." },
    { id: "E-7",  text: "Identify and manage conflicts of interest." },
    { id: "E-8",  text: "Provide supervision and mentorship per BACB standards." },
    { id: "E-9",  text: "Coordinate with other professionals." },
    { id: "E-10", text: "Use social media and new technologies in ways that protect client welfare and the field." },
  ],
  coreConcepts: [
    {
      title: "What is ethics in ABA",
      body: [
        "Ethics defines specific rules of conduct. Two complementary approaches: DEONTOLOGICAL ('acts-oriented'), which evaluates the act itself, and UTILITARIAN ('results-oriented'), which evaluates the consequences. Both contribute to ethical conduct.",
      ],
    },
    {
      title: "Three fundamental questions",
      body: [
        "Ethics addresses three questions: (1) What is the right thing to do? (2) What is worth doing? (3) What does it mean to be a good behavior analyst?",
      ],
    },
    {
      title: "Why ethics matters",
      body: [
        "Ethics helps practitioners decide whether a course of action is appropriate or inappropriate, including in the presence of outside pressures or expediencies. Ethical practices ensure the well-being of (a) clients, (b) the profession, and (c) the culture as a whole.",
      ],
    },
    {
      title: "Codes of conduct and professional standards",
      body: [
        "Behavior analysts are guided by the Professional and Ethical Compliance Code for Behavior Analysts (the BACB Compliance Code), the Ethical Principles of Psychologists and Code of Conduct, the Right to Effective Behavioral Treatment, Statement on Restraint and Seclusion, and the Right to Effective Education. Perceived violations can be reported via a notice of alleged violation.",
      ],
    },
    {
      title: "Professional competence: training and scope",
      body: [
        "Professional competence is achieved through FORMAL ACADEMIC TRAINING (coursework, supervised practica, mentored professional experience). Competence in supervision is critical. Behavior analysts MUST PRACTICE WITHIN THEIR AREAS OF COMPETENCE, training, and experience. New competencies are developed via supervised or mentored experience.",
      ],
    },
    {
      title: "Maintaining and extending competence",
      body: [
        "Behavior analysts maintain and expand competencies through continuing education, professional conference participation, current professional literature, and peer review committees. Truthful and accurate communication is required in all professional and personal interactions.",
      ],
    },
    {
      title: "Informed consent",
      body: [
        "Three tests must be met for valid informed consent: (1) CAPACITY TO DECIDE, (2) VOLUNTARY DECISION, and (3) KNOWLEDGE OF THE TREATMENT. All three must be satisfied; otherwise consent is not valid.",
      ],
    },
    {
      title: "Confidentiality",
      body: [
        "Confidentiality requires that the behavior analyst not discuss or release information about the client. Information may be released only with FORMAL PERMISSION of the individual or guardian.",
      ],
    },
    {
      title: "Client rights",
      body: [
        "Practicing behavior analysts have a responsibility to protect a client's dignity, health, and safety. Rights include: the right to make choices, the right to privacy, the right to a therapeutic treatment environment, and the right to refuse treatment.",
      ],
    },
    {
      title: "Negligence and fraudulent conduct",
      body: [
        "NEGLIGENCE (not doing what one should) and FRAUDULENT CONDUCT (willful deceit likely to cause harm) pose direct threats to client health and safety. Both can result in professional sanctions and legal penalties.",
      ],
    },
    {
      title: "Coordinating with other professionals",
      body: [
        "Behavior analysts must coordinate services in a way that meets the client's interests and represents the field positively. Effective coordination requires CLEAR and FREQUENT COMMUNICATION among all treatment team members.",
      ],
    },
    {
      title: "Social media and new technologies",
      body: [
        "Social media and new technologies create opportunities AND ethical pitfalls. Protect client confidentiality; avoid case-specific treatment recommendations in public forums. Telepractice tools allow remote viewing for supervisors and trainees - use them within confidentiality safeguards.",
      ],
    },
    {
      title: "Advocating for the client",
      body: [
        "Before providing services, the analyst must determine that services are needed, medical causes have been ruled out, the environment will support delivery, and a reasonable expectation of success exists. Advocate for the MOST EFFECTIVE, SCIENTIFICALLY SUPPORTED treatments. Account for SOCIAL VALIDITY. Objectively evaluate alternative treatments.",
      ],
    },
    {
      title: "Conflict of interest",
      body: [
        "A conflict of interest occurs when a principal party (alone or with family, friends, associates) has a vested interest in the outcome. All sources of conflict, particularly DUAL RELATIONSHIPS, are to be avoided.",
      ],
    },
    {
      title: "Culture of ethical practice",
      body: [
        "Organizations can foster ethical practice by providing ethics-related trainings, support systems, and DIFFERENTIAL REINFORCEMENT of ethical behaviors. Continuous education and discussion about ethics and the contingencies that support analysis and help-seeking are core features of an environment designed to maintain ethical behavior.",
      ],
    },
  ],
  glossary: [
    { term: "Ethics", definition: "Specific rules of conduct that guide professional decisions for behavior analysts.", sourceHint: "For the applied behavior analyst, ethics defines specific rules of conduct." },
    { term: "Deontological ethics", definition: "Acts-oriented approach that evaluates the act itself.", sourceHint: "the deontological or 'acts-oriented' approach, which evaluates only the act itself." },
    { term: "Utilitarian ethics", definition: "Results-oriented approach that evaluates the consequences of the act.", sourceHint: "the utilitarian or 'results-oriented' approach, which looks at the results or consequences of the act." },
    { term: "BACB Compliance Code", definition: "Professional and Ethical Compliance Code for Behavior Analysts; the primary code of conduct.", sourceHint: "Behavior analysts are now held to The Compliance Code." },
    { term: "Informed consent", definition: "Consent meeting three tests: capacity to decide, voluntary decision, and knowledge of the treatment.", sourceHint: "Three tests must be met before informed consent can be considered valid: capacity to decide, voluntary decision, and knowledge of the treatment." },
    { term: "Capacity to decide", definition: "The client's ability to make an informed decision; one of three tests for valid consent.", sourceHint: "Three tests must be met before informed consent can be considered valid: capacity to decide." },
    { term: "Confidentiality", definition: "Professional standard requiring not to discuss or release information about a client without formal permission.", sourceHint: "Confidentiality refers to the professional standard requiring that the behavior analyst not discuss or otherwise release information regarding someone in his or her care." },
    { term: "Scope of competence", definition: "The areas in which a behavior analyst has competence, training, and experience; defines what services they may provide.", sourceHint: "Behavior analysts must practice within their areas of competence, training, and experience." },
    { term: "Negligence", definition: "Not doing what one should; a direct threat to client safety that can result in sanctions.", sourceHint: "Negligence (not doing what one should) and fraudulent conduct (willful deceit likely to cause harm) on the part of the behavior analyst pose a direct threat to the client's health and safety." },
    { term: "Fraudulent conduct", definition: "Willful deceit likely to cause harm; subject to professional and legal sanctions.", sourceHint: "fraudulent conduct (willful deceit likely to cause harm) on the part of the behavior analyst pose a direct threat to the client's health and safety." },
    { term: "Right to effective treatment", definition: "Client's right to receive treatments shown by research to be effective; withholding may be unethical.", sourceHint: "The behavior analyst should advocate for the most effective, scientifically supported treatment procedures." },
    { term: "Right to refuse treatment", definition: "Client right to decline treatment; protected alongside dignity, health, and safety rights.", sourceHint: "Various rights must be observed and protected, including the right to make choices, the right to privacy, the right to a therapeutic treatment environment, and the right to refuse treatment." },
    { term: "Conflict of interest", definition: "Situation in which a principal party has a vested interest in the outcome that may compromise judgment.", sourceHint: "A conflict of interest occurs when a principal party, alone or in connection with family, friends, or associates, has a vested interest in the outcome of the interaction." },
    { term: "Dual relationship", definition: "Relationship in which the analyst has multiple roles with a client; a source of conflict of interest to avoid.", sourceHint: "All sources of conflict of interest, particularly dual relationships, are to be avoided." },
    { term: "Notice of alleged violation", definition: "The BACB mechanism for reporting perceived violations of the Compliance Code.", sourceHint: "Behavior analysts are now held to The Compliance Code, and perceived violations of that code can be reported using a notice of alleged violation." },
    { term: "Culture of ethical practice", definition: "Organizational environment that provides ethics-related training, support systems, and differential reinforcement of ethical behaviors.", sourceHint: "To foster a culture of ethical practice, organizations can commit to providing ethics-related trainings, support systems, and differential reinforcement of ethical behaviors." },
  ],
  procedures: [
    {
      title: "Obtain valid informed consent",
      intro: "Verify all three tests before proceeding.",
      steps: [
        { title: "Capacity to decide", detail: "Confirm the individual has the cognitive and legal capacity to make this decision (or work through a legal guardian)." },
        { title: "Voluntary decision", detail: "Ensure the decision is free from coercion or undue pressure." },
        { title: "Knowledge of the treatment", detail: "Provide complete, accurate information about the treatment, alternatives, risks, and benefits." },
        { title: "Document", detail: "Record consent in writing per BACB and jurisdictional requirements." },
        { title: "Revisit periodically", detail: "Renew consent when treatment changes substantially or per agency policy." },
      ],
    },
    {
      title: "Evaluate whether to provide services",
      intro: "Two decision rules: amenability and likely success.",
      steps: [
        { title: "Determine that services are needed", detail: "Confirm the problem warrants behavior-analytic intervention." },
        { title: "Rule out medical causes", detail: "Medical evaluation should precede behavioral intervention for new problem behavior." },
        { title: "Assess the environment", detail: "Confirm the treatment environment will support service delivery." },
        { title: "Reasonable expectation of success", detail: "Estimate likelihood the intervention will achieve the goal." },
        { title: "Decision rules", detail: "(1) Is the problem amenable to behavioral intervention? (2) Is the intervention likely to succeed?" },
      ],
    },
    {
      title: "Manage conflicts of interest",
      intro: "Anticipate, disclose, and avoid.",
      steps: [
        { title: "Identify potential conflicts", detail: "Family, friends, business partners, financial stakes." },
        { title: "Avoid dual relationships", detail: "Do not serve as a behavior analyst to friends or family." },
        { title: "Disclose promptly", detail: "Communicate any identified conflict to supervisors and clients as appropriate." },
        { title: "Recuse or transfer", detail: "If conflict cannot be managed, transfer the case to another qualified provider." },
      ],
    },
  ],
  examples: [
    { scenario: "Analyst is asked to provide treatment to a close friend's child.",
      application: "Avoid the dual relationship; refer to another qualified provider.",
      sourceHint: "All sources of conflict of interest, particularly dual relationships, are to be avoided." },
    { scenario: "A clinician shares an interesting case description on social media with no client identifiers.",
      application: "Still problematic - clinicians should avoid case-specific treatment recommendations in public forums even when de-identified.",
      sourceHint: "Special care must be taken to protect client confidentiality, and the behavior analyst should avoid making case-specific treatment recommendations in a public forum." },
    { scenario: "A behavior analyst is asked to provide services in an area outside their training; they decline and refer to a more experienced colleague.",
      application: "Practicing within scope of competence.",
      sourceHint: "Behavior analysts must practice within their areas of competence, training, and experience." },
    { scenario: "A treatment team includes an OT, speech therapist, and BCBA who meet weekly to align goals.",
      application: "Coordinating services with other professionals via clear, frequent communication.",
      sourceHint: "Effective coordination between disciplines can only occur if communication among all treatment team members remains clear and frequent." },
  ],
  comparisons: [
    {
      title: "Deontological vs Utilitarian ethics",
      left:  { label: "Deontological", points: [
        "Acts-oriented: evaluates the act itself.",
        "Asks whether the act is permissible.",
      ]},
      right: { label: "Utilitarian", points: [
        "Results-oriented: evaluates consequences.",
        "Asks whether outcomes are best.",
      ]},
    },
    {
      title: "Negligence vs Fraudulent conduct",
      left:  { label: "Negligence", points: [
        "Not doing what one should.",
        "Often involves omission or insufficient care.",
      ]},
      right: { label: "Fraudulent conduct", points: [
        "Willful deceit likely to cause harm.",
        "Active wrongdoing; subject to legal penalties.",
      ]},
    },
    {
      title: "Right to effective treatment vs Right to refuse treatment",
      left:  { label: "Right to effective treatment", points: [
        "Client should receive scientifically supported procedures.",
        "Withholding can itself be unethical.",
      ]},
      right: { label: "Right to refuse treatment", points: [
        "Client may decline treatment.",
        "Behavior analyst respects choice; documents informed refusal.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "Ethics is just a list of rules to memorize.",
      reality: "Ethics requires judgment and balances acts-oriented and results-oriented approaches; codes provide guidance, not algorithms.",
      sourceHint: "Two approaches to ethics are termed the deontological or 'acts-oriented' approach... and the utilitarian or 'results-oriented' approach... Both approaches contribute to the ethical conduct of behavior analysts.",
    },
    {
      myth: "Confidentiality applies only to written records.",
      reality: "Confidentiality applies to ALL discussion and release of information; permission is required to release information in any form.",
      sourceHint: "Confidentiality refers to the professional standard requiring that the behavior analyst not discuss or otherwise release information regarding someone in his or her care. Information may be released only with formal permission of the individual or the individual's guardian.",
    },
    {
      myth: "If a parent agrees, informed consent is valid.",
      reality: "Three tests are required: capacity, voluntary decision, AND knowledge of the treatment. Lacking any of the three invalidates consent.",
      sourceHint: "Three tests must be met before informed consent can be considered valid: capacity to decide, voluntary decision, and knowledge of the treatment.",
    },
    {
      myth: "It is fine to take on cases outside your scope if you read up first.",
      reality: "Practicing outside scope of competence is an ethical violation. New competencies require supervised or mentored experience.",
      sourceHint: "Behavior analysts must practice within their areas of competence, training, and experience. Behavior analysts can develop additional competencies through supervised or mentored experience.",
    },
    {
      myth: "Social media is a useful place to share clinical anecdotes.",
      reality: "Avoid case-specific treatment recommendations in public forums. Protect confidentiality at all times.",
      sourceHint: "Special care must be taken to protect client confidentiality, and the behavior analyst should avoid making case-specific treatment recommendations in a public forum.",
    },
  ],
  examChecklist: [
    "Define ethics and distinguish deontological from utilitarian approaches.",
    "State the three fundamental ethics questions.",
    "List the major professional codes and statements guiding ABA practice.",
    "Describe the three tests for valid informed consent.",
    "Define confidentiality and the rule for release of information.",
    "List four client rights.",
    "Distinguish negligence from fraudulent conduct.",
    "Describe scope of competence and how new competencies are developed.",
    "Define conflict of interest and dual relationship.",
    "List four social media / new technology safeguards.",
    "Describe how organizations create a culture of ethical practice.",
  ],
  flashcardTerms: [
    "Ethics", "Deontological ethics", "Utilitarian ethics",
    "BACB Compliance Code",
    "Informed consent", "Capacity to decide",
    "Confidentiality", "Scope of competence",
    "Negligence", "Fraudulent conduct",
    "Right to effective treatment", "Right to refuse treatment",
    "Conflict of interest", "Dual relationship",
    "Notice of alleged violation", "Culture of ethical practice",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Which is NOT one of the three tests for valid informed consent?",
      options: [
        { label: "A", text: "Capacity to decide" },
        { label: "B", text: "Voluntary decision" },
        { label: "C", text: "Knowledge of the treatment" },
        { label: "D", text: "Approval by all family members" },
      ],
      answer: "D",
      explanation: "Family approval is not a test for valid consent; the three tests are capacity, voluntary decision, and knowledge of the treatment.",
      topic: "Informed consent",
      sourceHint: "Three tests must be met before informed consent can be considered valid: capacity to decide, voluntary decision, and knowledge of the treatment.",
    },
    {
      id: "Q2",
      question: "Confidentiality requires that:",
      options: [
        { label: "A", text: "Behavior analysts can share client info informally with friends" },
        { label: "B", text: "Information may be released only with formal permission of the individual or guardian" },
        { label: "C", text: "All clinical notes be made public" },
        { label: "D", text: "Information may be shared anonymously online" },
      ],
      answer: "B",
      explanation: "Information about a client may be released only with formal permission of the individual or their guardian.",
      topic: "Confidentiality",
      sourceHint: "Information may be released only with formal permission of the individual or the individual's guardian.",
    },
    {
      id: "Q3",
      question: "Which approach to ethics evaluates the consequences of an act?",
      options: [
        { label: "A", text: "Deontological" },
        { label: "B", text: "Utilitarian" },
        { label: "C", text: "Categorical" },
        { label: "D", text: "Naturalistic" },
      ],
      answer: "B",
      explanation: "Utilitarian (results-oriented) ethics evaluates the consequences. Deontological (acts-oriented) evaluates the act itself.",
      topic: "Ethical approaches",
      sourceHint: "the utilitarian or 'results-oriented' approach, which looks at the results or consequences of the act.",
    },
    {
      id: "Q4",
      question: "Which is a SOURCE of conflict of interest to be avoided?",
      options: [
        { label: "A", text: "Supervision of a colleague" },
        { label: "B", text: "Dual relationships (e.g., providing services to a friend)" },
        { label: "C", text: "Reviewing peer-reviewed literature" },
        { label: "D", text: "Continuing education credits" },
      ],
      answer: "B",
      explanation: "Dual relationships are a primary source of conflict of interest and are to be avoided.",
      topic: "Conflict of interest",
      sourceHint: "All sources of conflict of interest, particularly dual relationships, are to be avoided.",
    },
    {
      id: "Q5",
      question: "Before providing services, the behavior analyst must determine:",
      options: [
        { label: "A", text: "That services are needed, medical causes are ruled out, the environment supports delivery, and success is reasonably expected" },
        { label: "B", text: "That the family will pay in full" },
        { label: "C", text: "That a competing analyst is not involved" },
        { label: "D", text: "That the BACB has pre-approved the case" },
      ],
      answer: "A",
      explanation: "Four conditions must be evaluated before deciding to provide services.",
      topic: "Decision to provide services",
      sourceHint: "In deciding to provide services, the behavior analyst must determine that services are needed, that medical causes have been ruled out, that the treatment environment will support service delivery, and that a reasonable expectation of success exists.",
    },
    {
      id: "Q6",
      question: "How should a behavior analyst develop competence in a new area?",
      options: [
        { label: "A", text: "Self-study; read journal articles" },
        { label: "B", text: "Attend one conference and start practicing" },
        { label: "C", text: "Through supervised or mentored experience" },
        { label: "D", text: "Simply add the new area to their resume" },
      ],
      answer: "C",
      explanation: "New competencies require supervised or mentored experience, not self-study alone.",
      topic: "Scope of competence",
      sourceHint: "Behavior analysts can develop additional competencies through supervised or mentored experience.",
    },
    {
      id: "Q7",
      question: "Which is a recommended way for organizations to foster a culture of ethical practice?",
      options: [
        { label: "A", text: "Punish ethical violations only" },
        { label: "B", text: "Provide ethics-related trainings, support systems, AND differential reinforcement of ethical behaviors" },
        { label: "C", text: "Limit ethics discussions to onboarding" },
        { label: "D", text: "Outsource ethics to a third-party agency" },
      ],
      answer: "B",
      explanation: "A culture of ethical practice depends on training, support systems, and differential reinforcement of ethical behaviors.",
      topic: "Culture of ethics",
      sourceHint: "To foster a culture of ethical practice, organizations can commit to providing ethics-related trainings, support systems, and differential reinforcement of ethical behaviors.",
    },
  ],
  reviewPrompts: [
    "Apply the three tests for informed consent to a real case.",
    "Identify a potential conflict of interest you might encounter and your plan to manage it.",
    "Sketch the four conditions you would evaluate before accepting a new case.",
    "Explain the difference between deontological and utilitarian ethics with one example.",
    "Plan an organizational culture-of-ethics intervention.",
    "Distinguish negligence from fraudulent conduct with one example each.",
    "List the rights you would protect for every client you serve.",
  ],
  recap: [
    "Ethics in ABA is a synthesis of deontological (acts) and utilitarian (results) reasoning. The BACB Compliance Code, the Ethical Principles of Psychologists, the Right to Effective Behavioral Treatment, the Statement on Restraint and Seclusion, and the Right to Effective Education are core guidance.",
    "Practice within your scope of competence. Maintain and expand competence via continuing education, mentorship, and peer review. Obtain valid informed consent (capacity + voluntary + knowledge). Protect confidentiality. Honor client rights including dignity, choice, privacy, therapeutic environment, and refusal.",
    "Coordinate with other professionals via clear and frequent communication. Use social media and new technologies with extreme care. Advocate for effective treatments and consider social validity. Avoid conflicts of interest including dual relationships. Build a culture of ethical practice with training, support, and differential reinforcement.",
  ],
  sourceExcerpts: [
    {
      heading: "Two ethical approaches",
      text: "Two approaches to ethics are termed the deontological or 'acts-oriented' approach, which evaluates only the act itself, and the utilitarian or 'results-oriented' approach, which looks at the results or consequences of the act. Both approaches contribute to the ethical conduct of behavior analysts.",
    },
    {
      heading: "Three tests for informed consent",
      text: "Three tests must be met before informed consent can be considered valid: capacity to decide, voluntary decision, and knowledge of the treatment.",
    },
    {
      heading: "Scope of competence",
      text: "Behavior analysts must practice within their areas of competence, training, and experience. Behavior analysts can develop additional competencies through supervised or mentored experience.",
    },
    {
      heading: "Decision to provide services",
      text: "In deciding to provide services, the behavior analyst must determine that services are needed, that medical causes have been ruled out, that the treatment environment will support service delivery, and that a reasonable expectation of success exists.",
    },
    {
      heading: "Culture of ethical practice",
      text: "To foster a culture of ethical practice, organizations can commit to providing ethics-related trainings, support systems, and differential reinforcement of ethical behaviors. Continuous education and discussion about ethics and the contingencies that support analysis and assistance seeking are core features of an environment designed to maintain ethical behavior in an organization.",
    },
  ],
};

export default chapter31Module;
